import { useState } from 'react';
import { useClients, useWorkspace, useWorkspaceById } from '../data/store';
import { Icon } from '../components/Icon';
import { Avatar, PlanChip, StatusChip } from '../components/bits';
import { euro } from '../data/helpers';
import type { ClientStatus } from '../data/types';

interface Props {
  onOpenClient: (id: string) => void;
}

const FILTERS: { id: 'all' | ClientStatus; label: string }[] = [
  { id: 'all', label: 'Tutti' },
  { id: 'live', label: 'Online' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'draft', label: 'Bozze' },
  { id: 'paused', label: 'Sospesi' },
  { id: 'archived', label: 'Archiviati' },
];

export function ClientsView({ onOpenClient }: Props) {
  const [filter, setFilter] = useState<'all' | ClientStatus>('all');
  const { current } = useWorkspace();
  const workspaceById = useWorkspaceById();
  const all = useClients();
  // Gli archiviati restano fuori da ogni vista tranne il filtro "Archiviati".
  const list = all.filter((c) =>
    filter === 'archived' ? c.status === 'archived' : c.status !== 'archived' && (filter === 'all' || c.status === filter),
  );
  const showPartner = current === 'all';

  return (
    <div className="view">
      <div className="view-pad">
        <div className="rs-toolbar">
          <div className="rs-seg">
            {FILTERS.map((f) => (
              <button key={f.id} className={filter === f.id ? 'on' : ''} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rs-table">
          <div className="rs-thead">
            <span>Cliente</span>
            <span>Piano</span>
            <span>Stato</span>
            <span className="rs-hide-sm">Dominio</span>
            <span className="num rs-hide-sm">MRR</span>
            <span className="rs-hide-sm">{showPartner ? 'Spazio' : 'Attività'}</span>
          </div>
          {list.map((c) => {
            const ws = workspaceById(c.workspaceId);
            return (
              <button type="button" className="rs-row" key={c.id} onClick={() => onOpenClient(c.id)}>
                <span className="rs-cli">
                  <Avatar client={c} />
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span className="rs-cli-name">{c.name}</span>
                      {c.ownerEdited && (
                        <span
                          className="rs-edited-mark"
                          role="img"
                          aria-label="Modificato dal cliente"
                          title="Il cliente ha modificato il sito — modifica nell'editor"
                        >
                          <Icon name="edit" size={12} />
                        </span>
                      )}
                    </span>
                    <span className="rs-cli-meta">
                      {c.category} · {c.locality}
                    </span>
                  </span>
                </span>
                <span className="rs-cell">
                  <PlanChip plan={c.plan} />
                </span>
                <span className="rs-cell">
                  <StatusChip status={c.status} />
                </span>
                <span className="rs-cell rs-hide-sm">
                  {c.domainStatus === 'none' ? <span className="rs-dim">—</span> : c.domain}
                </span>
                <span className="rs-cell num rs-hide-sm">
                  {c.isDemo ? <span className="rs-chip demo">demo</span> : c.mrr ? euro(c.mrr) : <span className="rs-dim">—</span>}
                </span>
                <span className="rs-cell rs-hide-sm">
                  {showPartner ? (
                    <span className="rs-ws-tag">
                      <span className="rs-ws-dot" style={{ background: ws?.accent }} />
                      {ws?.name.replace(' (house)', '')}
                    </span>
                  ) : (
                    <span className="rs-dim">{c.lastActivity}</span>
                  )}
                </span>
              </button>
            );
          })}
          {list.length === 0 && (
            <div className="rs-muted" style={{ padding: '24px 18px', fontSize: 13 }}>
              Nessun cliente in questo stato.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
